import React, { PureComponent } from "react";
import styled from "styled-components";
import _ from "lodash";

// Unified audit-trail event table. Renders a normalized stream of
// {when, who, action, target, status, detail, round} entries with a
// filter bar (action chips + search) and a per-row expander that
// surfaces the role-specific detail object.
//
// Consumes events emitted by the role getters in page.js — no role-
// aware rendering lives here, so the same component renders CEO,
// PD, PM, OP, and FN streams.

const FONT = "'Mukta Vaani', Lato, sans-serif";

const Wrap = styled.div`
  font-family: ${FONT};
`;

const FiltersBar = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  align-items: center;
`;

const Search = styled.input`
  flex: 1;
  min-width: 220px;
  padding: 10px 14px;
  border: 1.5px solid #E5E9F0;
  border-radius: 10px;
  background: #F5F7FB;
  font-size: 14px;
  font-family: ${FONT};
  color: rgba(0, 27, 59, 0.94);
  outline: none;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:focus {
    border-color: #3A507B;
    background: #FFFFFF;
  }
`;

const ChipGroup = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const Chip = styled.button`
  padding: 8px 14px;
  border: 1.5px solid ${p => (p.active ? "#3A507B" : "#E5E9F0")};
  background: ${p => (p.active ? "#3A507B" : "#FFFFFF")};
  color: ${p => (p.active ? "#FFFFFF" : "rgba(0, 27, 59, 0.7)")};
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: ${FONT};
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

  &:hover {
    border-color: #3A507B;
  }
`;

const ResultCount = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 27, 59, 0.5);
  margin-left: auto;
`;

const Card = styled.div`
  background: #FFFFFF;
  border: 1px solid #EEF0F4;
  border-radius: 12px;
  overflow: hidden;
`;

// 6-column grid: When | Who | Description | Amount | Status | Chevron.
// Fixed columns are sized to their widest realistic content (timestamp
// "Jun 1, 2026 · 10:32 AM", names like "Utest 8", "$1,234,567", status
// badges); the Description column then claims everything left over.
const Row = styled.div`
  display: grid;
  grid-template-columns: 150px 110px minmax(0, 1fr) 110px 90px 28px;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #F2F4F8;
  align-items: center;
  cursor: ${p => (p.clickable ? "pointer" : "default")};
  background: ${p => (p.open ? "#FAFBFD" : "transparent")};
  transition: background 0.15s ease;

  &:hover {
    background: ${p => (p.clickable ? "#FAFBFD" : "transparent")};
  }
`;

const HeaderRow = styled(Row)`
  background: #FAFBFD;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(0, 27, 59, 0.5);
  cursor: default;

  &:hover {
    background: #FAFBFD;
  }
`;

const Cell = styled.div`
  font-size: 14px;
  color: rgba(0, 27, 59, 0.94);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Description cell wraps onto additional lines instead of truncating —
// the full sentence is the row's most informative piece of content, so
// readability beats single-line uniformity here.
const DescriptionCell = styled.div`
  font-size: 14px;
  color: rgba(0, 27, 59, 0.94);
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
  line-height: 1.4;

  strong {
    font-weight: 700;
    color: rgba(0, 27, 59, 1);
  }
`;

// Render a description string with **markdown-style bold** support.
// Splits on `**...**` segments and wraps the inner text in <strong>.
// We use this only for the table row — search/filter continues to
// match against the raw string since `includes` ignores the asterisks
// around the keyword.
const renderDescription = text => {
  if (text == null) return null;
  const str = String(text);
  const parts = str.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    const m = part.match(/^\*\*(.+)\*\*$/);
    if (m) return <strong key={i}>{m[1]}</strong>;
    return part;
  });
};

const ActionBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 700;
  border-radius: 6px;
  background: ${p => p.bg};
  color: ${p => p.fg};
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 700;
  border-radius: 6px;
  background: ${p => p.bg};
  color: ${p => p.fg};
`;

const DetailPanel = styled.div`
  padding: 14px 24px 18px 24px;
  background: #FAFBFD;
  border-bottom: 1px solid #F2F4F8;
`;

const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(0, 27, 59, 0.5);
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 8px 24px;
`;

const DetailItem = styled.div`
  font-size: 13px;
  color: rgba(0, 27, 59, 0.82);
`;

const DetailKey = styled.span`
  font-weight: 700;
  color: rgba(0, 27, 59, 0.5);
  margin-right: 6px;
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.06em;
`;

const Empty = styled.div`
  padding: 60px 24px;
  text-align: center;
  color: rgba(0, 27, 59, 0.4);
  font-size: 14px;
`;

const Chevron = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 27, 59, 0.4);
  font-size: 18px;
  font-weight: 700;
  transform: ${p => (p.open ? "rotate(90deg)" : "rotate(0)")};
  transition: transform 0.15s ease;
`;

// Visual palette per action verb. Colors stay muted so the badge reads
// at a glance without competing with the row content.
const ACTION_COLORS = {
  Created:   { bg: "#E0F2E9", fg: "#1F7A3E" },
  Updated:   { bg: "#E3ECFA", fg: "#1F4FB6" },
  Deleted:   { bg: "#FCE4E4", fg: "#9F1D1D" },
  Submitted: { bg: "#EFE7FE", fg: "#5B3DAF" },
  Posted:    { bg: "#FFF4D6", fg: "#8A5B00" },
};

const STATUS_COLORS = {
  success: { bg: "#E0F2E9", fg: "#1F7A3E" },
  failure: { bg: "#FCE4E4", fg: "#9F1D1D" },
  neutral: { bg: "#EFF1F6", fg: "rgba(0, 27, 59, 0.55)" },
};

const ACTION_OPTIONS = ["All", "Created", "Updated", "Deleted", "Submitted"];

class EventTable extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      search: "",
      action: "All",
      expanded: {},
    };
  }

  onSearchChange = e => this.setState({ search: e.target.value });

  setAction = action => () => this.setState({ action });

  toggleExpand = id => () => this.setState(s => ({
    expanded: { ...s.expanded, [id]: !s.expanded[id] },
  }));

  render() {
    const { events = [] } = this.props;
    const { search, action, expanded } = this.state;
    const q = search.trim().toLowerCase();

    const filtered = events.filter(ev => {
      if (action !== "All" && ev.action !== action) return false;
      if (q) {
        const hay = `${ev.who} ${ev.description || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    const sorted = _.sortBy(filtered, ev => -ev.unix);

    return (
      <Wrap>
        <FiltersBar>
          <Search
            type="text"
            placeholder="Search by user or target…"
            value={search}
            onChange={this.onSearchChange}
          />
          <ChipGroup>
            {ACTION_OPTIONS.map(a => (
              <Chip
                key={a}
                active={action === a}
                onClick={this.setAction(a)}
              >{a}</Chip>
            ))}
          </ChipGroup>
          <ResultCount>{sorted.length} event{sorted.length === 1 ? "" : "s"}</ResultCount>
        </FiltersBar>

        <Card>
          <HeaderRow>
            <Cell>When</Cell>
            <Cell>Who</Cell>
            <Cell>Description</Cell>
            <Cell>Amount</Cell>
            <Cell>Status</Cell>
            <Cell />
          </HeaderRow>

          {sorted.length === 0 && (
            <Empty>No activity to show for this round.</Empty>
          )}

          {sorted.map(ev => {
            const isOpen = !!expanded[ev.id];
            const actionColor = ACTION_COLORS[ev.action] || ACTION_COLORS.Posted;
            const statusColor = STATUS_COLORS[ev.status.type] || STATUS_COLORS.neutral;
            const detailEntries = ev.detail
              ? Object.keys(ev.detail).filter(k => ev.detail[k] != null && ev.detail[k] !== "")
              : [];
            // Detail panel is always available — even rows with no
            // detail items still surface the Action badge there now.
            const hasDetail = true;
            return (
              <div key={ev.id}>
                <Row
                  open={isOpen}
                  clickable={hasDetail}
                  onClick={this.toggleExpand(ev.id)}
                >
                  <Cell>{ev.when}</Cell>
                  <Cell>{ev.who}</Cell>
                  <DescriptionCell>{renderDescription(ev.description)}</DescriptionCell>
                  <Cell>{ev.amount || "—"}</Cell>
                  <Cell>
                    <StatusBadge bg={statusColor.bg} fg={statusColor.fg}>
                      {ev.status.label}
                    </StatusBadge>
                  </Cell>
                  <Cell>
                    <Chevron open={isOpen}>›</Chevron>
                  </Cell>
                </Row>
                {isOpen && (
                  <DetailPanel>
                    <DetailHeader>
                      <span>Action</span>
                      <ActionBadge bg={actionColor.bg} fg={actionColor.fg}>
                        {ev.action}
                      </ActionBadge>
                    </DetailHeader>
                    {detailEntries.length > 0 && (
                      <DetailGrid>
                        {detailEntries.map(k => (
                          <DetailItem key={k}>
                            <DetailKey>{k}</DetailKey>
                            {ev.detail[k]}
                          </DetailItem>
                        ))}
                      </DetailGrid>
                    )}
                  </DetailPanel>
                )}
              </div>
            );
          })}
        </Card>
      </Wrap>
    );
  }
}

export default EventTable;



// WEBPACK FOOTER //
// ./src/components/audit-trail/event-table.js